import { Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'

import { LeaseDetailService } from '@modules/private/lease/data/services/view/lease-detail.service'
import { riskImportWarnings } from '@modules/private/lease/data/utils/risk-import.util'

/**
 * ADR-0021 — Subida del Excel estandarizado "Análisis de Riesgos - Rendafin v1".
 *
 * Comportamiento "one-shot":
 * - Antes del primer upload: muestra dropzone con advertencia de inmutabilidad.
 * - Después del upload: muestra solo botón de descarga + mensaje "ya no editable".
 *   El usuario NO ve la opción de re-subir.
 */
@Component({
	selector: 'app-risk-excel-upload',
	templateUrl: './risk-excel-upload.component.html',
	styleUrls: ['./risk-excel-upload.component.scss'],
	providers: [MessageService, ConfirmationService],
})
export class RiskExcelUploadComponent {
	@Output() imported = new EventEmitter<void>()
	@ViewChild('reuploadInput') reuploadInput!: ElementRef<HTMLInputElement>

	isUploading = false
	isReuploading = false
	selectedFileName: string | null = null

	constructor(
		public leaseDetailService: LeaseDetailService,
		private readonly messageService: MessageService,
		private readonly confirmationService: ConfirmationService,
	) {}

	get hasImportedData(): boolean {
		// Señal de "ya importado" válida para PM y PF. PF no trae `eeffPeriods`, así
		// que se confía en `importedAt`/`excelFileUrl` (presentes en todo import
		// ADR-0021) y en los pilares de la calculadora.
		const r = this.leaseDetailService.ratiosData as any
		const c = this.leaseDetailService.calculatorData as any
		const hasEeff = Array.isArray(r?.eeffPeriods) && r.eeffPeriods.length > 0
		const hasPillars = Array.isArray(c?.pillars) && c.pillars.length > 0
		return !!(r?.importedAt || r?.excelFileUrl || hasEeff || hasPillars)
	}

	get excelFileUrl(): string | null {
		const r = this.leaseDetailService.ratiosData as any
		return r?.excelFileUrl || null
	}

	get excelFileName(): string {
		const r = this.leaseDetailService.ratiosData as any
		return r?.excelFileName || 'Análisis de Riesgo.xlsx'
	}

	get importedAt(): Date | null {
		const r = this.leaseDetailService.ratiosData as any
		return r?.importedAt ? new Date(r.importedAt) : null
	}

	isDownloading = false

	downloadExcel(): void {
		if (!this.hasImportedData) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Sin archivo',
				detail: 'No hay un Excel original disponible para descargar.',
			})
			return
		}
		this.isDownloading = true
		this.leaseDetailService.downloadRiskExcel().subscribe({
			next: (response: any) => {
				this.isDownloading = false
				const blob = response.body as Blob | null
				if (!blob) {
					this.messageService.add({
						severity: 'error',
						summary: 'Descarga vacía',
						detail: 'El servidor no devolvió contenido.',
					})
					return
				}
				const filename = this.extractFilenameFromResponse(response) || this.excelFileName || 'analisis-riesgo.xlsx'
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = filename
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				setTimeout(() => URL.revokeObjectURL(url), 1000)
			},
			error: (err: any) => {
				this.isDownloading = false
				console.error('Error al descargar Excel:', err)
				this.messageService.add({
					severity: 'error',
					summary: 'Error al descargar',
					detail: err?.error?.msg || 'No se pudo descargar el archivo. Reintenta en un momento.',
					life: 6000,
				})
			},
		})
	}

	private extractFilenameFromResponse(response: any): string | null {
		const cd = response?.headers?.get?.('content-disposition') || response?.headers?.get?.('Content-Disposition')
		if (!cd) return null
		// "attachment; filename=\"foo.xlsx\"; filename*=UTF-8''foo.xlsx"
		const matchStar = /filename\*=UTF-8''([^;]+)/i.exec(cd)
		if (matchStar) return decodeURIComponent(matchStar[1])
		const match = /filename="?([^";]+)"?/i.exec(cd)
		return match ? match[1] : null
	}

	onUpload(event: { files: File[] }): void {
		const file = event?.files?.[0]
		if (!file) {
			this.messageService.add({ severity: 'warn', summary: 'Sin archivo', detail: 'Selecciona un archivo .xlsx primero.' })
			return
		}
		this.uploadFile(file)
	}

	private uploadFile(file: File): void {
		this.selectedFileName = file.name
		this.isUploading = true

		this.leaseDetailService.importRiskExcel(file).subscribe({
			next: (response: any) => {
				this.isUploading = false
				if (response?.error) {
					this.messageService.add({
						severity: 'error',
						summary: 'Error al importar',
						detail: response?.msg || 'No se pudo procesar el Excel. Verifica que sea el template Rendafin v1.',
						life: 8000,
					})
					return
				}
				this.messageService.add({
					severity: 'success',
					summary: 'Excel importado',
					detail: 'El análisis de riesgo se importó correctamente. Los datos ya no son editables.',
					life: 6000,
				})
				this.showImportWarnings(response)
				this.imported.emit()
			},
			error: (err: any) => {
				this.isUploading = false
				this.messageService.add({
					severity: 'error',
					summary: 'Error inesperado',
					detail: err?.message || 'Falló el upload del archivo.',
					life: 8000,
				})
			},
		})
	}

	/**
	 * Confirma el reemplazo del Excel antes de abrir el selector de archivo.
	 * El backend borra los datos y blob anteriores tras guardar los nuevos
	 * (ADR-0021 § "Actualización 2026-05-28"). Solo Admin y Finanzas ven el
	 * botón que dispara esto (gating por `risk.re-upload` en el template).
	 */
	confirmReupload(): void {
		this.confirmationService.confirm({
			header: 'Reemplazar análisis de riesgo',
			message:
				'Esto reemplazará el análisis actual y <strong>borrará el Excel anterior</strong>. La acción no se puede deshacer. ¿Deseas continuar?',
			icon: 'fa-solid fa-triangle-exclamation',
			acceptLabel: 'Reemplazar',
			rejectLabel: 'Cancelar',
			acceptButtonStyleClass: 'p-button-warning',
			rejectButtonStyleClass: 'p-button-text',
			accept: () => this.reuploadInput?.nativeElement?.click(),
		})
	}

	onReuploadFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement
		const file = input.files?.[0]
		if (!file) return
		if (!file.name.toLowerCase().endsWith('.xlsx')) {
			this.messageService.add({ severity: 'warn', summary: 'Tipo no soportado', detail: 'Selecciona un archivo .xlsx del template Rendafin v1.' })
			input.value = ''
			return
		}

		this.selectedFileName = file.name
		this.isReuploading = true

		this.leaseDetailService.replaceRiskExcel(file).subscribe({
			next: (response: any) => {
				this.isReuploading = false
				input.value = ''
				if (response?.error) {
					this.messageService.add({
						severity: 'error',
						summary: 'Error al reemplazar',
						detail: response?.msg || 'No se pudo procesar el Excel. Verifica que sea el template Rendafin v1.',
						life: 8000,
					})
					return
				}
				this.messageService.add({
					severity: 'success',
					summary: 'Excel reemplazado',
					detail: 'El análisis de riesgo se reemplazó correctamente. Los datos anteriores fueron eliminados.',
					life: 6000,
				})
				this.showImportWarnings(response)
				this.imported.emit()
			},
			error: (err: any) => {
				this.isReuploading = false
				input.value = ''
				this.messageService.add({
					severity: 'error',
					summary: 'Error inesperado',
					detail: err?.message || 'Falló el reemplazo del archivo.',
					life: 8000,
				})
			},
		})
	}

	/** F-026 · La importación se guardó, pero el Excel trajo algo que no se pudo leer o que se ignoró: se dice. */
	private showImportWarnings(response: unknown): void {
		const warnings = riskImportWarnings(response as any)
		if (!warnings.length) return
		this.messageService.add({ severity: 'warn', summary: 'Importado con avisos', detail: warnings.join(' · '), life: 15000 })
	}
}
